import pluralize from "pluralize";
import { useState, ReactNode } from "react";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import { setNonSeatedStoreState } from "~features/nonseated-reservation/store/non-seated-reservation-store";
import { FooterState } from "~features/nonseated-reservation/components/ExperienceSalesFooter/types";
import { CreateOrderResultOrder } from "~hooks/reservation/types";
import { ErrorMessage } from "~hooks/reservation/useCreateOrder";

interface FooterActionsProps {
  handleGAOrderCreation: (
    tickets: IReleaseTicket[],
    addons: IReleaseTicket[],
    multiBuyId?: string,
    errorCallback?: (message: ErrorMessage, title: string) => void,
    onSuccess?: (order: CreateOrderResultOrder) => Promise<void> | void,
    options?: {
      skipRedirectToCheckout?: boolean;
    }
  ) => Promise<boolean | void>;
  selectedTickets: IReleaseTicket[];

  showDowngradeConfirmation?: boolean;
  isExchangeFlow: boolean;
  skipRedirectToCheckout: boolean;
  multibuyId: string;
  // Added to access owned ticket data
  ownedTicketData?: {
    getTicketsByIds?: {
      id: string;
      lineItem?: {
        id: string;
        price: number;
      };
    }[];
  };
  onOrderCreated?: (order: CreateOrderResultOrder) => void;
}

/**
 * Hook to manage footer actions and state
 * Handles submission, error states, and action handlers
 */
export const useFooterActions = ({
  handleGAOrderCreation,
  selectedTickets,
  multibuyId,
  showDowngradeConfirmation,
  isExchangeFlow,
  skipRedirectToCheckout,
  ownedTicketData,
  onOrderCreated,
}: FooterActionsProps): FooterState => {
  const [submitting, setSubmitting] = useState(false);
  const [alertError, setAlertError] = useState<
    { title: string; message: string | ReactNode } | undefined
  >();

  const resetState = () => {
    setSubmitting(false);
    setNonSeatedStoreState({ isSubmitting: false });
  };

  const handlePrimaryAction = async (): Promise<void> => {
    const hasSelection = selectedTickets.length > 0;
    if (!hasSelection) return;

    try {
      // For ticket exchanges, validate that we're exchanging for the same number of tickets
      if (isExchangeFlow) {
        // Get number of tickets being purchased (excluding addons)
        const ticketItems = selectedTickets.filter(
          (tt) => tt.type === "ticket"
        );
        const totalTicketQuantity = ticketItems.reduce(
          (sum, ticket) => sum + ticket.quantity,
          0
        );

        // Determine the number of owned tickets
        let ownedTicketCount = 0;

        // First try to use the owned ticket data object if available
        if (
          ownedTicketData?.getTicketsByIds &&
          Array.isArray(ownedTicketData.getTicketsByIds)
        ) {
          ownedTicketCount = ownedTicketData.getTicketsByIds.length;
        } else {
          // Fallback to using the URL query parameter
          const router = new URL(window.location.href);
          const ownedTicketsParam = router.searchParams.get("ownedTickets");
          ownedTicketCount = ownedTicketsParam
            ? ownedTicketsParam.split(",").length
            : 0;
        }

        if (totalTicketQuantity !== ownedTicketCount) {
          setAlertError({
            title:
              ownedTicketCount > totalTicketQuantity
                ? "Not enough tickets selected"
                : "Too many tickets selected",
            message: `This exchange is for ${ownedTicketCount} ${pluralize(
              "ticket",
              ownedTicketCount
            )}, ${
              totalTicketQuantity < ownedTicketCount
                ? `add ${
                    ownedTicketCount - totalTicketQuantity
                  } more ${pluralize(
                    "ticket",
                    ownedTicketCount - totalTicketQuantity
                  )}`
                : `remove ${totalTicketQuantity - ownedTicketCount} ${pluralize(
                    "ticket",
                    totalTicketQuantity - ownedTicketCount
                  )}`
            } and try again.`,
          });
          return;
        }
      }

      // Set submitting state
      setNonSeatedStoreState({ isSubmitting: true });
      setSubmitting(true);

      // Create the order
      const result = await handleGAOrderCreation(
        selectedTickets.filter((tt) => tt.type === "ticket") ?? [],
        selectedTickets.filter((tt) => tt.type === "addon") ?? [],
        multibuyId,
        (errorMessage: ErrorMessage, errorTitle: string) => {
          setAlertError({
            title: errorTitle,
            message:
              typeof errorMessage === "object"
                ? errorMessage.plainText
                : errorMessage,
          });
        },
        onOrderCreated,
        {
          skipRedirectToCheckout,
        }
      );

      // If false, a confirmation dialog is showing, don't reset state
      if (result === false) {
        return;
      }
    } catch (error) {
      console.error("Unhandled error in order creation:", error);
      setAlertError({
        title: "Unexpected error",
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      // Reset state unless waiting for confirmation
      if (!showDowngradeConfirmation) {
        resetState();
      }
    }
  };

  return {
    submitting,
    alertError,
    setAlertError,
    resetState,
    handlePrimaryAction,
  };
};
